/**
 * Grid Component
 *
 * A layout primitive for CSS grid-based layouts with responsive presets.
 * Uses Tailwind's grid utilities (no arbitrary values).
 *
 * Features:
 * - Responsive column presets (cards, features, gallery)
 * - Static column counts (1-6, 12)
 * - Auto-fit columns for dynamic layouts
 * - Gap scale using Tailwind spacing
 *
 * @example Basic grid
 * ```tsx
 * <Grid cols={3} gap="lg">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 *   <Card>Item 3</Card>
 * </Grid>
 * ```
 *
 * @example Responsive preset
 * ```tsx
 * <Grid preset="cards" gap="lg">
 *   {products.map(product => <ProductCard key={product.id} />)}
 * </Grid>
 * ```
 *
 * @example Custom responsive columns
 * ```tsx
 * <Grid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} gap="md">
 *   {items.map(item => <ItemCard key={item.id} />)}
 * </Grid>
 * ```
 */

import * as React from 'react';
import type {VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';
import {GRID_PRESETS, gridItemVariants, type GridPreset, gridVariants} from './Grid.variants';

// =============================================================================
// TYPES
// =============================================================================

export interface ResponsiveCols {
  base?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  sm?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  md?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  lg?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
  xl?: 1 | 2 | 3 | 4 | 5 | 6 | 12;
}

interface GridProps
  extends React.HTMLAttributes<HTMLElement>,
    Omit<VariantProps<typeof gridVariants>, "cols"> {
  /**
   * Number of columns - can be static, responsive object, or undefined for presets
   */
  cols?: VariantProps<typeof gridVariants>["cols"] | ResponsiveCols;
  /**
   * Pre-defined responsive column configuration
   * Use presets instead of custom responsive cols when possible
   */
  preset?: GridPreset;
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?: "div" | "section" | "article";
}

// =============================================================================
// HELPERS
// =============================================================================

function buildResponsiveColsClasses(cols: ResponsiveCols): string {
  const classes: string[] = [];

  if (cols.base) classes.push(`grid-cols-${cols.base}`);
  if (cols.sm) classes.push(`sm:grid-cols-${cols.sm}`);
  if (cols.md) classes.push(`md:grid-cols-${cols.md}`);
  if (cols.lg) classes.push(`lg:grid-cols-${cols.lg}`);
  if (cols.xl) classes.push(`xl:grid-cols-${cols.xl}`);

  return classes.join(" ");
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Grid - CSS Grid layout primitive
 *
 * Use Grid for any grid-based layouts. Prefer presets over custom
 * responsive configurations when possible.
 *
 * Pages and features MUST use this instead of ad-hoc grid containers.
 *
 * Replaces patterns like:
 * - `className="grid grid-cols-1 md:grid-cols-2 gap-6"`
 * - `className="grid grid-cols-3 gap-4"`
 * - `className="grid grid-cols-2 lg:grid-cols-4 gap-8"`
 */
const Grid = React.memo(function Grid({
  as: Component = "div",
  cols,
  preset,
  gap,
  align,
  justify,
  fullWidth,
  className,
  children,
  ...props
}: GridProps): React.ReactElement {
  // Determine column classes
  let colsClasses: string | undefined;

  if (preset) {
    // Use preset configuration
    colsClasses = GRID_PRESETS[preset];
  } else if (typeof cols === "object" && cols !== null) {
    // Build responsive cols classes
    colsClasses = buildResponsiveColsClasses(cols);
  }

  return (
    <Component
      className={cn(
        gridVariants({
          cols:
            !preset && typeof cols !== "object"
              ? (cols as VariantProps<typeof gridVariants>["cols"])
              : undefined,
          gap,
          align,
          justify,
          fullWidth,
        }),
        colsClasses,
        className
      )}
      data-layout="grid"
      data-preset={preset}
      {...props}
    >
      {children}
    </Component>
  );
});

Grid.displayName = "Grid";

// =============================================================================
// GRID ITEM COMPONENT
// =============================================================================


interface GridItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof gridItemVariants> {}

/**
 * GridItem - Child of Grid for spanning multiple columns/rows
 */
const GridItem = React.memo(function GridItem({
  colSpan,
  rowSpan,
  className,
  children,
  ...props
}: GridItemProps): React.ReactElement {
  return (
    <div
      className={cn(gridItemVariants({ colSpan, rowSpan }), className)}
      {...props}
    >
      {children}
    </div>
  );
});

GridItem.displayName = "GridItem";

// =============================================================================
// EXPORTS
// =============================================================================


export { Grid, GridItem,    };
;
